/**
 * Gap list hook + CUD helpers.
 *
 * The hook re-fetches whenever the filter object (stringified) changes, or
 * whenever the returned ``refresh`` is called. Callers should invoke
 * ``refresh()`` after a CUD mutation so the UI reflects the new truth.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  ApiError,
  createGap as apiCreateGap,
  deleteGap as apiDeleteGap,
  listGaps as apiListGaps,
  patchGap as apiPatchGap,
} from '../api';
import type {
  Gap,
  GapCreateInput,
  GapPatchInput,
} from '../types';

import type { GapFilters } from '../api';

export function useGaps(filters: GapFilters): {
  gaps: Gap[];
  error: string | null;
  loading: boolean;
  refresh: () => void;
} {
  const key = useMemo(() => JSON.stringify(filters), [filters]);
  const [gaps, setGaps] = useState<Gap[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiListGaps(filters)
      .then((g) => {
        if (!cancelled) {
          setGaps(g);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(describe(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `filters` identity changes per render but `key` is the stable signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  return { gaps, error, loading, refresh };
}

export async function createGap(payload: GapCreateInput): Promise<Gap> {
  return apiCreateGap(payload);
}

export async function patchGap(id: string, patch: GapPatchInput): Promise<Gap> {
  return apiPatchGap(id, patch);
}

export async function deleteGap(id: string): Promise<void> {
  await apiDeleteGap(id);
}

function describe(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = e.detail;
    if (detail && typeof detail === 'object' && 'detail' in detail) {
      const inner = (detail as { detail: unknown }).detail;
      if (typeof inner === 'string') return inner;
    }
    return `${e.status}: ${e.message}`;
  }
  return e instanceof Error ? e.message : String(e);
}
